import { OChainId } from '@lombard.finance/sdk';
import BigNumber from 'bignumber.js';
import { Days, Milliseconds, Minutes, Seconds, TEnv } from './types';

export const ROOT_PATH = '/';

export const ACTION_CACHE: Seconds = 60;
export const ACTION_CACHE_LONG: Seconds = 600;

export const metaEnv = import.meta.env;
export const CURRENT_ENV = metaEnv.VITE_ENV as TEnv;
export const IS_PROD = CURRENT_ENV === 'prod';
export const IS_STAGE = CURRENT_ENV === 'stage' || !IS_PROD;
export const IS_LOCAL = metaEnv.VITE_IS_LOCAL === 'true';
export const NAME = metaEnv.VITE_NAME;
export const VERSION = metaEnv.VITE_VERSION;

export const DEFAULT_CHAIN_ID = IS_PROD ? OChainId.ethereum : OChainId.holesky;
// Vault only supports Ethereum mainnet for now
export const DEFAULT_VAULT_CHAIN_ID = OChainId.ethereum;

export const DECIMAL_PLACES_ZERO = 0;
export const DECIMAL_PLACES = 4;
export const USD_DECIMAL_PLACES = 2;
export const DECIMAL_PLACES_SHORT = 2;
export const DECIMAL_PLACES_LUX = 0;
export const DECIMAL_PLACES_BTC = 6;
export const ETH_DECIMALS = 18;
export const ETH_SCALE = 10 ** ETH_DECIMALS;
export const ERC20_TOKEN_DECIMALS = ETH_DECIMALS;
export const BTC_DECIMALS = 8;
export const SATOSHI_SCALE = 10 ** BTC_DECIMALS;

export const HOURS_PER_DAY: Milliseconds = 24;

export const SECONDS_PER_DAY: Milliseconds = HOURS_PER_DAY * 60 * 60;

export const MS_PER_DAY: Milliseconds = SECONDS_PER_DAY * 1000;

export const HUNDRED_PERCENT = 100;

export const ZERO = new BigNumber(0);
export const ONE = new BigNumber(1);

export const DEFAULT_BTC_PRICE = 0;

/**
 * Address of the zero account.
 * Can also be used as a placeholder for unknown addresses.
 */
export const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/**
 * Address of the native token.
 */
export const NATIVE_TOKEN_ADDRESS =
  '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee';

export const WEBSITE_LINK = IS_PROD
  ? 'https://www.lombard.finance'
  : 'https://stage.lombard.finance';

export const TWITTER_LINK = 'https://x.com/lombard_finance';

export const LOMBARD_DOCS_LINK = 'https://docs.lombard.finance/';
export const LOMBARD_FAQ_LINK = `${LOMBARD_DOCS_LINK}frequently-asked-questions/faqs/lombard-faqs`;
export const LOMBARD_FAQ_BTC_ADDRESS_LINK = `${LOMBARD_DOCS_LINK}frequently-asked-questions/faqs/lbtc-faqs#what-addresses-are-supported-for-unstaking`;
export const LOMBARD_DOCS_REFERRALS_LINK = `${LOMBARD_DOCS_LINK}lbtc-liquid-bitcoin/lux-and-luminary-program/referral-program`;

export const TERMS_OF_SERVICE_LINK = `${LOMBARD_DOCS_LINK}legals/terms-of-service`;

export const LOMBARD_PRIVACY_POLICY_LINK = `${LOMBARD_DOCS_LINK}legals/privacy-policy`;

export const UNSTAKE_INFO_LINK = `${LOMBARD_DOCS_LINK}faq/lbtc-faq#can-i-withdraw-my-btc-from-lombard`;

export const OKX_DOC_LINK = `${LOMBARD_DOCS_LINK}frequently-asked-questions/faqs/okx-wallet-faq`;

export const SUPPORT_EMAIL = 'support@lombard.finance';

export const UNSTAKING_PERIOD: Days = 7;
export const UNSTAKING_PERIOD_MS: Milliseconds = UNSTAKING_PERIOD * MS_PER_DAY;
export const UNSTAKING_TEXT = `${UNSTAKING_PERIOD} days`;
export const SECURITY_FEE = new BigNumber(IS_PROD ? 0.0001 : 0.0002);
export const EXPECTED_NOTARIZATION_WAIT_DURATION: Seconds = 60 * 60;

export const FEE_UPDATE_INTERVAL: Milliseconds = 30_000;

export const MIN_DEPOSIT_AMOUNT = new BigNumber(0.000001);

export const SNACKBAR_ZINDEX = 1400;

export const DELAY_FOR_PAGE_LOADING = 700;

/**
 * The site key for the CAPTCHA service.
 */
export const CAPTCHA_SITE_KEY = IS_PROD
  ? '6Lfd-zQqAAAAAOSx_TNizFLhMOiyMQkes0QYb2aj'
  : '6LeLrTIqAAAAADiubgJFZP6hmZwtd5cTGScqWvxU';

export const BTC_BLOCK_MINING_TIME: Minutes = 10;
export const STAKE_FEE = '0.00';
export const BTC_LBTC_RATIO_TEXT = '1 BTC = 1 LBTC';

/**
 * The referral ID for internal requests
 */
export const REFFERAL_ID = 'lombard';

export const NETWORK_FEE_EXPIRATION_TIME = IS_STAGE
  ? 60 * 60 * 5 // Expiration time is a minute for testing purpose
  : SECONDS_PER_DAY;

const confirmationsByEnv: Record<TEnv, number> = {
  prod: 6,
  testnet: 6,
  stage: 3,
};

/**
 * The number of confirmations required for a transaction to be considered final.
 */
export const TOTAL_CONFIRMATIONS = confirmationsByEnv[CURRENT_ENV] || 0;

export const OCountryCode = {
  US: 'US',
  CU: 'CU',
  IR: 'IR',
  SY: 'SY',
  KP: 'KP',
  VE: 'VE',
  BY: 'BY',
  RU: 'RU',
} as const;

export const RESTRICTED_COUNTRIES_CODES = [
  OCountryCode.US,
  OCountryCode.CU,
  OCountryCode.IR,
  OCountryCode.SY,
  OCountryCode.KP,
  OCountryCode.VE,
  OCountryCode.BY,
  OCountryCode.RU,
];